export const ACCESS_STATUS_DEGRADED_POLL_MS = 30_000;
export const CHAT_SOCKET_HEARTBEAT_MS = 30_000;
export const SPEAKER_STATUS_HEALTHY_POLL_MS = 30_000;
export const SPEAKER_STATUS_DEGRADED_POLL_MS = 5_000;

const POLL_JITTER_RATIO = 0.2;

const clampUnitInterval = (value) => Math.max(0, Math.min(1, Number(value) || 0));

/**
 * Add positive jitter so a large audience does not refresh in one synchronized
 * wave after mounting or reconnecting at the same time.
 */
export const addControlPlaneJitter = (baseDelayMs, randomValue = Math.random()) => (
  Math.round(baseDelayMs * (1 + (POLL_JITTER_RATIO * clampUnitInterval(randomValue))))
);

export const getAccessStatusPollDelay = (socketConnected, randomValue = Math.random()) => (
  socketConnected
    ? null
    : addControlPlaneJitter(ACCESS_STATUS_DEGRADED_POLL_MS, randomValue)
);

export const getSpeakerStatusPollDelay = (socketConnected, randomValue = Math.random()) => (
  addControlPlaneJitter(
    socketConnected ? SPEAKER_STATUS_HEALTHY_POLL_MS : SPEAKER_STATUS_DEGRADED_POLL_MS,
    randomValue,
  )
);

const newestTimestamp = (current, value) => {
  if (!value || !Number.isFinite(Date.parse(value))) return current;
  if (!current || !Number.isFinite(Date.parse(current))) return value;
  return Date.parse(value) > Date.parse(current) ? value : current;
};

/**
 * Track a durable recovery cursor from the latest chat/reaction snapshot. The
 * backend currently tolerates the optional `after` parameter; this keeps the
 * client ready for cursor-filtered responses without changing the UI contract.
 */
export const advanceChatRecoveryCursor = (currentCursor, payload = {}) => {
  const entries = [
    ...(Array.isArray(payload.messages) ? payload.messages : []),
    ...(Array.isArray(payload.questions) ? payload.questions : []),
    ...(Array.isArray(payload.reactions) ? payload.reactions : []),
  ];
  return entries.reduce(
    (cursor, entry) => newestTimestamp(cursor, entry?.created_at || entry?.timestamp),
    currentCursor || null,
  );
};

export const getChatRecoveryParams = (cursor, limit = 50) => ({
  limit,
  ...(cursor ? { after: cursor } : {}),
});

export const isAccessRevocationSocketClose = (code) => Number(code) === 4003;

/**
 * Passive viewers watch IVS. A selected Call-In/Raise Hand/Mic participant
 * opens LiveKit only as an audio publishing bridge; it must not subscribe to
 * or render the LiveKit program because doing so competes with the IVS player
 * and can interrupt or resize the viewer display.
 */
export const getViewerMediaHandoffPolicy = ({
  speakerSelected = false,
  ivsFallbackRequired = false,
  isProMode = false,
} = {}) => ({
  needsLiveKitConnection: Boolean(speakerSelected || ivsFallbackRequired),
  shouldRenderLiveKitStage: Boolean(ivsFallbackRequired),
  shouldAutoSubscribe: Boolean(ivsFallbackRequired && !isProMode),
  isSpeakerBridgeOnly: Boolean(speakerSelected && !ivsFallbackRequired),
});
